import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { ScrollArea } from "./ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  ThumbsUp,
  Edit3,
  Save,
  X,
  MessageSquare,
  Check,
  Undo2,
} from "lucide-react";
import { useToast } from "../hooks/use-toast";
import {
  ConfirmedKey,
  MeasureData,
  MeasurementKey,
} from "../../pt/utils/interfaces";
import {
  allMeasurementsFemale,
  allMeasurementsMale,
} from "../../common/constants";
import { on } from "events";

interface FeedbackDialogProps {
  measureData: MeasureData;
  gender: "male" | "female";
  onUpdateMeasurement: (measurement: MeasureData) => void;
}

export const FeedbackDialog = ({
  measureData,
  gender,
  onUpdateMeasurement,
}: FeedbackDialogProps) => {
  const { toast } = useToast();
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState("");
  // clone measure data to avoid direct mutation, these will be updated via callbacks
  const [updateMeasurement, setUpdateMeasurement] = useState<MeasureData>({
    ...measureData,
  });
  const [confirmedMeasurements, setConfirmedMeasurements] = useState<
    Set<number>
  >(new Set());
  const [savedMeasurements, setSavedMeasurements] = useState<Set<number>>(
    new Set()
  );

  // Filter to only show measurements with cm/inches units and get most recent for each title
  const filteredMeasurements = measureData ? [measureData] : [];
  const keysAll =
    gender === "female" ? allMeasurementsFemale : allMeasurementsMale;

  const handleEditMeasurement = (
    editableIndex: number,
    currentValue: number
  ) => {
    setEditingIndex(editableIndex);
    setEditValue(Math.round(currentValue).toString());
  };
  const getValues = (
    key: string
  ): [boolean, boolean, boolean, number | undefined, number | undefined] => {
    // Original value from the scan
    const original =
      measureData.measurements[key as keyof typeof measureData.measurements];
    // Value from the confirmedMeasurements, ie saved to backend
    const confirmed = measureData.confirmedMeasurements
      ? measureData.confirmedMeasurements[
          key as keyof typeof measureData.confirmedMeasurements
        ]
      : undefined;
    // value from the current editable state

    const updated = updateMeasurement?.confirmedMeasurements
      ? (updateMeasurement.confirmedMeasurements[
          key as keyof typeof updateMeasurement.confirmedMeasurements
        ] as number | undefined)
      : undefined;

    const isModified = updated !== undefined && updated !== confirmed;
    const isConfirmed = updateMeasurement !== undefined && updated === original;
    const isSaved = !isConfirmed && updated !== undefined;

    return [isConfirmed, isSaved, isModified, original, updated];
  };
  const hasChanges = keysAll.some((key) => {
    if (updateMeasurement.confirmedMeasurements === undefined) {
      return false;
    }
    if (
      key.key === "weight" ||
      key.key === "height" ||
      key.key === "body_fat"
    ) {
      return false;
    }
    const updatedValue = updateMeasurement.confirmedMeasurements[key.key];
    const originalValue = measureData.confirmedMeasurements
      ? measureData.confirmedMeasurements[key.key as ConfirmedKey]
      : undefined;
    return updatedValue !== originalValue;
  });

  const handleSaveMeasurement = (key: MeasurementKey) => {
    if (editingIndex !== null) {
      const newValue = parseFloat(editValue);
      if (!isNaN(newValue) && newValue > 0) {
        const update = {
          confirmedMeasurements: {
            ...updateMeasurement.confirmedMeasurements,
            [key]: newValue,
          },
        } as Partial<MeasureData>;
        setUpdateMeasurement((prev) => ({ ...prev, ...update }));

        setEditingIndex(null);
        setEditValue("");
      } else {
        toast({
          title: "Invalid value",
          description: "Please enter a valid positive number.",
          variant: "destructive",
        });
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditValue("");
  };

  const handleConfirmMeasurement = (key: MeasurementKey, label: string) => {
    // set updated measurement to original scanned value
    const originalValue = measureData.measurements[key];
    const previousConfirmed = updateMeasurement.confirmedMeasurements
      ? { ...updateMeasurement.confirmedMeasurements }
      : ({} as any);

    if (originalValue === undefined) {
      toast({
        title: "Error",
        description: `Original value for ${key} not found.`,
        variant: "destructive",
      });
      return;
    }
    const updateValue = originalValue;

    const update = {
      confirmedMeasurements: {
        ...previousConfirmed,
        ...{
          [key]: updateValue,
        },
      },
    } as Partial<MeasureData>;
    setUpdateMeasurement((prev) => ({ ...prev, ...update }));
  };

  const handleUndoConfirmation = (key: MeasurementKey) => {
    if (updateMeasurement.confirmedMeasurements) {
      const updatedConfirmed = { ...updateMeasurement.confirmedMeasurements };
      delete updatedConfirmed[key as ConfirmedKey];
      setUpdateMeasurement((prev) => ({
        ...prev,
        confirmedMeasurements: updatedConfirmed,
      }));
    }

    toast({
      title: "Confirmation undone",
      description: "Measurement confirmation has been removed.",
    });
  };

  const handleUndoSave = (key: MeasurementKey) => {
    if (updateMeasurement.confirmedMeasurements) {
        // Create a new confirmedMeasurements object
        const updatedConfirmed = { ...updateMeasurement.confirmedMeasurements };
        
        // Delete the specific key from the new object
        delete updatedConfirmed[key as ConfirmedKey];
        
        // Update the state with the new object that has the key removed
        setUpdateMeasurement(prev => ({
            ...prev,
            confirmedMeasurements: updatedConfirmed
        }));
        
        console.log(`Removed ${key} from confirmedMeasurements`, updatedConfirmed);
    }
  };

  const handleSubmit = () => {
    onUpdateMeasurement(updateMeasurement);
    toast({
      title: "Measurements submitted",
      description:
        "All confirmed and modified measurements have been submitted successfully.",
    });
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <MessageSquare className="w-4 h-4 mr-2" />
          Feedback
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Measurement Feedback</DialogTitle>
          <DialogDescription>
            Review and confirm your scanned measurements. Edit any values that
            seem incorrect or validate them with a thumbs up.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {false ? (
            <div className="text-center py-8 space-y-4">
              <ThumbsUp className="w-12 h-12 mx-auto text-green-500" />
              <div>
                <h3 className="text-lg font-medium">
                  All measurements confirmed!
                </h3>
                <p className="text-sm text-muted-foreground">
                  All your measurements have been validated and are ready to be
                  saved.
                </p>
              </div>
            </div>
          ) : (
            <ScrollArea className="h-96 w-full rounded border">
              <div className="space-y-3 p-4">
                {keysAll
                  .filter(
                    (key) => !["weight", "height", "body_fat"].includes(key.key)
                  )
                  .map((key, index) => {
                    const label = key.label;
                    const measureKey = key.key;
                    const unit = key.unit;
                    const [
                      isConfirmed,
                      isSaved,
                      isModified,
                      original,
                      updated,
                    ] = getValues(measureKey);
                    const showValue = updated ?? original ?? 0;

                    return (
                      <div
                        key={key.key}
                        className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${
                          isConfirmed || isSaved
                            ? "bg-green-50 border-green-200"
                            : "bg-background"
                        }`}
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-sm">{label}</div>
                            <div className="text-xs text-muted-foreground">
                              {original &&
                                `Scanned: ${Math.round(original)} ${unit}`}
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            {editingIndex === index ? (
                              <div className="flex items-center gap-2">
                                <Input
                                  type="number"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  className="w-24 h-8 text-sm"
                                  step="1"
                                  min="0"
                                />
                                <span className="text-sm text-muted-foreground min-w-fit">
                                  {unit}
                                </span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() =>
                                    handleSaveMeasurement(measureKey)
                                  }
                                >
                                  <Save className="w-3 h-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={handleCancelEdit}
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium min-w-fit">
                                  {showValue && Math.round(showValue)} {unit}
                                </span>

                                {!isConfirmed && !isSaved && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() =>
                                        handleEditMeasurement(
                                          index,
                                          showValue || 0
                                        )
                                      }
                                    >
                                      <Edit3 className="w-3 h-3" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() =>
                                        handleConfirmMeasurement(
                                          measureKey,
                                          label
                                        )
                                      }
                                      className="text-green-600 hover:text-green-700"
                                    >
                                      <ThumbsUp className="w-3 h-3" />
                                    </Button>
                                  </>
                                )}

                                {isConfirmed && (
                                  <div className="flex items-center gap-1">
                                    <div className="flex items-center gap-1 text-green-600">
                                      <ThumbsUp className="w-3 h-3" />
                                      <span className="text-xs">Confirmed</span>
                                    </div>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleUndoSave(key.key)}
                                      className="text-orange-600 hover:text-orange-700"
                                    >
                                      <Undo2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                )}

                                {isSaved && (
                                  <div className="flex items-center gap-1">
                                    <div className="flex items-center gap-1 text-green-600">
                                      <Check className="w-3 h-3" />
                                      <span className="text-xs">Saved</span>
                                    </div>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleUndoSave(key.key)}
                                      className="text-orange-600 hover:text-orange-700"
                                    >
                                      <Undo2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </ScrollArea>
          )}
        </div>

        <DialogFooter>
          <DialogTrigger asChild>
            <Button variant="outline">Cancel</Button>
          </DialogTrigger>
          <DialogTrigger asChild>
            <Button disabled={!hasChanges} onClick={handleSubmit}>
              Confirm Changes
            </Button>
          </DialogTrigger>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
